// PWA (DESIGN §7, этап 6 — полировка): установка на главный экран, связь с интернетом, обновление версии.
// Событие beforeinstallprompt приходит один раз и рано — ловим его при загрузке модуля, до React.
import { useSyncExternalStore } from 'react';

interface InstallEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface PwaState {
  /** Браузер готов показать своё окно установки (Chrome, Edge, Android). */
  installEvent: InstallEvent | null;
  /** Открыто как установленное приложение. */
  installed: boolean;
  online: boolean;
  /** Новая версия платформы уже работает в фоне — страницу стоит обновить. */
  updated: boolean;
}

const standalone = () =>
  typeof window !== 'undefined' &&
  (window.matchMedia?.('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true);

/** Страница открыта меньше этого — новую версию включаем перезагрузкой без вопроса. */
const FRESH_MS = 20_000;

let state: PwaState = {
  installEvent: null,
  installed: standalone(),
  online: typeof navigator === 'undefined' ? true : navigator.onLine,
  updated: false,
};
const listeners = new Set<() => void>();
const set = (patch: Partial<PwaState>) => {
  state = { ...state, ...patch };
  for (const l of listeners) l();
};

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    // Своя кнопка вместо мини-плашки браузера
    e.preventDefault();
    set({ installEvent: e as InstallEvent });
  });
  window.addEventListener('appinstalled', () => set({ installEvent: null, installed: true }));
  window.addEventListener('online', () => set({ online: true }));
  window.addEventListener('offline', () => set({ online: false }));
  // Новый service worker взял страницу под контроль — вышла новая версия. Первая установка — не обновление.
  // Страницу только что открыли (перезагрузили) или её не видно — перезагружаем сами: владелец сразу видит новую
  // версию. Уже работает на странице — плашка «Обновить» (NetBar): набранный текст не пропадёт.
  const sw = navigator.serviceWorker;
  if (sw) {
    const hadController = !!sw.controller;
    const openedAt = Date.now();
    let reloading = false;
    sw.addEventListener?.('controllerchange', () => {
      if (!hadController || reloading) return;
      if (document.visibilityState === 'hidden' || Date.now() - openedAt < FRESH_MS) {
        reloading = true;
        window.location.reload();
      } else set({ updated: true });
    });
    // Проверить, не вышла ли новая версия: при возврате в приложение (на телефоне его не закрывают — сам браузер
    // проверяет только при открытии) и раз в полчаса, пока вкладка открыта
    const check = () => {
      if (!navigator.onLine) return;
      void sw
        .getRegistration?.()
        .then((r) => r?.update())
        .catch(() => {});
    };
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') check();
    });
    window.setInterval(check, 30 * 60_000);
  }
  // Старая вкладка просит кусок кода, которого после выкатки уже нет, — перезагрузить один раз
  window.addEventListener('vite:preloadError', (e) => {
    try {
      if (sessionStorage.getItem('aaff-reloaded') === '1') return;
      sessionStorage.setItem('aaff-reloaded', '1');
    } catch {
      return;
    }
    e.preventDefault();
    window.location.reload();
  });
  window.addEventListener('load', () => {
    try {
      sessionStorage.removeItem('aaff-reloaded');
    } catch {}
  });
}

export function usePwa(): PwaState {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => state,
    () => state,
  );
}

/** Показать окно установки браузера; итог — установили или нет. */
export async function promptInstall(): Promise<boolean> {
  const e = state.installEvent;
  if (!e) return false;
  await e.prompt();
  const choice = await e.userChoice;
  set({ installEvent: null, ...(choice.outcome === 'accepted' ? { installed: true } : {}) });
  return choice.outcome === 'accepted';
}

export const isIos = () => typeof navigator !== 'undefined' && /iPhone|iPad|iPod/.test(navigator.userAgent);
